import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../shared/services/app.service';
import { ApiService } from '../../../shared/services/api.service';
import {
  DashboardMetadata,
  Engagements,
  GetAggregateResultQuery,
  GetAggregateResultQueryVariables,
  GetChartsQuery,
  GetChartsQueryVariables,
  GetDashboardQuery,
  GetDashboardQueryVariables,
  ResultAction,
} from '../../../../API';
import { GetAggregateResult, GetCharts, GetDashboard } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ChartDataWithId, ChartMetadataExtended } from '../../models/charts';
import { ChartDataService } from '../../services/chart-data.service';
import { DashboardPreviewComponent } from '../dashboard-preview/dashboard-preview.component';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-create-dashboard',
  templateUrl: './create-dashboard.component.html',
  styleUrl: './create-dashboard.component.scss',
})
export class CreateDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('track', { static: false }) track!: ElementRef<HTMLDivElement>;
  @ViewChild(DashboardPreviewComponent) dashboardPreviewComponent?: DashboardPreviewComponent;
  private destroy$ = new Subject<void>();

  engagement!: string;

  dashboardDetails: FormGroup;
  isLoading: boolean = false;

  charts: ChartMetadataExtended[] = [];
  chartsToDisplay: ChartMetadataExtended[] = [];
  selectedCharts: ChartMetadataExtended[] = [];
  allChartsResultData: ChartDataWithId = {};

  activeDashboardData?: DashboardMetadata;
  isEditMode: boolean = false;

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private chartDataService: ChartDataService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.dashboardDetails = this.fb.group({
      name: this.fb.control('', [Validators.required, Validators.maxLength(30)]),
      description: this.fb.control('', [Validators.required, Validators.maxLength(120)]),
      searchValue: this.fb.control(''),
    });
  }

  ngOnInit(): void {
    this.appService.activeEngagement$.pipe(takeUntil(this.destroy$)).subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
      this.getAllCharts();
    });

    const dashboardId = this.activatedRoute.snapshot.paramMap.get('dashboardId');
    if (dashboardId) {
      this.isEditMode = true;
      this.getDashboardData(dashboardId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.chartDataService.allChartsResultData.next({});
    this.chartDataService.selectedChartsForDashboard.next([]);
  }

  private sortCharts(): void {
    this.chartsToDisplay = this.charts.sort((a: any, b: any) => {
      const dateA = new Date(a.lastModifiedAt || 0);
      const dateB = new Date(b.lastModifiedAt || 0);

      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
  }

  reset(): void {
    this.searchValueFormControl?.reset();
    this.chartsToDisplay = [...this.charts];
  }

  search(): void {
    const searchValueLower = this.searchValueFormControl?.value.toLowerCase();
    this.chartsToDisplay = this.charts.filter((chart: any) => {
      const chartName = chart.name?.toLowerCase() || '';
      const chatType = chart.chartType?.toLowerCase() || '';
      const lastModifiedBy = chart.lastModifiedBy?.toLowerCase() || '';
      const createdBy = chart.createdBy?.toLowerCase() || '';
      const chartTitle = JSON.parse(chart.config)?.chartTitle || '';

      return (
        chartName.includes(searchValueLower) ||
        chatType.includes(searchValueLower) ||
        lastModifiedBy.includes(searchValueLower) ||
        createdBy.includes(searchValueLower) ||
        chartTitle.includes(searchValueLower)
      );
    });
  }

  scrollNext() {
    const track = this.track.nativeElement;
    const firstItem = track.querySelector('.carousel-item') as HTMLElement;
    if (firstItem) {
      track.scrollBy({ left: firstItem.offsetWidth, behavior: 'smooth' });
    }
  }

  scrollPrev() {
    const track = this.track.nativeElement;
    const firstItem = track.querySelector('.carousel-item') as HTMLElement;
    if (firstItem) {
      track.scrollBy({ left: -firstItem.offsetWidth, behavior: 'smooth' });
    }
  }

  getAllCharts(): void {
    this.isLoading = true;
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        engagementId: this.engagement,
      })
      .then((response) => {
        const result: ChartMetadataExtended[] = (response?.data?.GetCharts || []).map((chartItem) => {
          const newItem = {
            ...chartItem,
            chartDisplayImage: `${this.appService.domainUrl}/images/chart/${this.engagement}/${chartItem?.chartId}/chart.svg`,
          };
          return newItem;
        }) as ChartMetadataExtended[];

        this.chartsToDisplay = this.charts = result;
        this.sortCharts();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  selectChart(chart: ChartMetadataExtended) {
    this.getSelectedChartQueryResult(chart);
  }

  isChartSelected(chartId: string): boolean {
    const existingCharts = this.chartDataService.selectedChartsForDashboard.value;
    return existingCharts.some((item) => item.chartId === chartId);
  }

  getSelectedChartQueryResult(chart: ChartMetadataExtended): void {
    this.isLoading = true;
    this.apiService
      .query<GetAggregateResultQuery, GetAggregateResultQueryVariables>(GetAggregateResult, {
        resultRequest: {
          engagement: this.engagement,
          queryId: chart.queryId!,
          athenaExecutionId: chart.athenaExecutionId!,
          action: ResultAction.GET_AGGREGATE_RESULT,
        },
      })
      .then((response) => {
        const data = response?.data?.GetAggregateResult;
        if (data) {
          const rowsData = JSON.parse(data.rows!);

          const chartPreviewData = rowsData.reduce((acc: any, obj: any) => {
            Object.entries(obj).forEach(([key, value]) => {
              if (!acc[key]) acc[key] = [];
              acc[key].push(value);
            });
            return acc;
          }, {});

          const existingData = this.chartDataService.allChartsResultData.value;
          this.chartDataService.allChartsResultData.next({
            ...existingData,
            [chart.chartId]: chartPreviewData,
          });

          const existingSelectedCharts = this.chartDataService.selectedChartsForDashboard.value;
          const isChartAlreadySelected = existingSelectedCharts.some((item) => item.chartId === chart.chartId);
          if (!isChartAlreadySelected) {
            this.chartDataService.selectedChartsForDashboard.next([...existingSelectedCharts, chart]);
          } else {
            this.dashboardPreviewComponent?.removeWidget(chart.chartId);
          }
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Error getting data for rendering chart ${error?.message || error}`);
      })
      .finally(() => (this.isLoading = false));
  }

  getDashboardData(dashboardId: string) {
    this.isLoading = true;
    this.apiService
      .query<GetDashboardQuery, GetDashboardQueryVariables>(GetDashboard, {
        dashboardId,
        engagementId: this.engagement,
      })
      .then((response) => {
        this.activeDashboardData = response.data?.GetDashboard as DashboardMetadata;
        this.dashboardDetails.patchValue({
          name: this.activeDashboardData.name,
          description: this.activeDashboardData.description,
        });

        if (this.activeDashboardData.chartIds?.length) {
          this.getChartsData(this.activeDashboardData.chartIds);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(
          `Unable to load dashboard for edit, ${error?.message || error} error getting dashboard data,`,
        );
      })
      .finally(() => (this.isLoading = false));
  }

  getChartsData(chartIds: string[]) {
    this.isLoading = true;
    this.apiService
      .query<GetChartsQuery, GetChartsQueryVariables>(GetCharts, {
        engagementId: this.engagement,
      })
      .then((response) => {
        const result = response.data?.GetCharts || [];
        const selectedCharts = result.filter((chart) => chart?.chartId && chartIds.includes(chart?.chartId));
        selectedCharts.forEach((chart) => {
          if (chart) {
            const extendedChart: ChartMetadataExtended = {
              ...chart,
              chartDisplayImage: `${this.appService.domainUrl}/images/chart/${this.engagement}/${chart?.chartId}/chart.svg`,
            };
            this.selectChart(extendedChart);
          }
        });
      })
      .catch((error) => {
        this.nzMessageService.error(
          `Unable to load dashboard for edit, ${error?.message || error} error getting charts data,`,
        );
      })
      .finally(() => (this.isLoading = false));
  }

  get searchValueFormControl(): UntypedFormControl {
    return this.dashboardDetails.get('searchValue') as UntypedFormControl;
  }
}
